import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { equipmentAPI, athleteAPI } from '../../services/api';
import { EquipmentFormData, PopulatedEquipment, Athlete } from '../../types/mongodb';
import { PageTitle } from '../CoachDashboard/styles';
import EquipmentForm from './EquipmentForm';
import { RecordsSection, RecordsTable, TableHeader, TableRow } from '../TrainingSessions/styles';
import { useAuth } from '../../contexts/AuthContext';

// Custom centered table cell
const CenteredTableCell = styled.td`
  padding: 0.9rem 0.7rem 0.9rem 0;
  color: var(--text-gray) !important;
  font-family: 'Poppins', sans-serif;
  font-size: 1.05rem;
  font-weight: 600;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  text-align: center;

  @media (max-width: 768px) {
    font-size: var(--mobile-list-cell, 0.65rem);
    padding: 0.25rem 0.4rem;
  }
`;

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2.2rem 1.2rem 1.2rem 1.2rem;
  color: var(--text-gray);
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding-left: 1rem;
    padding-right: 1rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1rem;
  color: var(--text-gray);
  font-weight: 400;
  margin-top: 0;
  margin-bottom: 1.2rem;
  text-align: left;
  font-family: 'Poppins', sans-serif;

  @media (max-width: 768px) {
    font-size: var(--mobile-paragraph, 0.75rem);
  }
`;

const ActionIcon = styled.button`
  background: none;
  border: none;
  color: var(--text-gray);
  font-size: 1rem;
  cursor: pointer;
  margin-left: 0.3rem;
  &:hover {
    color: var(--accent-orange);
  }
`;

const EditIcon = styled(ActionIcon)`
  &:hover {
    color: var(--accent-orange);
  }
`;

const DeleteIcon = styled(ActionIcon)`
  &:hover {
    color: #ff6b6b;
  }
`;

const AddNewEquipmentButton = styled.button`
  background: var(--accent-orange);
  color: #fff;
  border: 2px solid var(--accent-orange);
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  font-weight: 800;
  font-family: 'Poppins', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(255, 150, 0, 0.3);
  width: 100%;
  max-width: 320px;

  &:hover {
    background: var(--accent-orange-dark);
    border-color: var(--accent-orange-dark);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 150, 0, 0.35);
  }

  &:active {
    transform: translateY(0);
  }
`;

const AddButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: var(--spacing-xl);
  color: var(--text-secondary);
  font-size: 1.1rem;
`;

const ErrorMessage = styled.div`
  background: #FFF5F5;
  border: 1px solid #FFE3E3;
  color: #E03131;
  padding: var(--spacing-md);
  border-radius: var(--radius-md);
  margin-bottom: var(--spacing-lg);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  box-shadow: var(--shadow-sm);
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-gray);
  font-style: italic;
  opacity: 0.7;
`;

const SortableTableHeader = styled(TableHeader)`
  cursor: pointer;
  user-select: none;
  transition: color 0.2s;
  
  &:hover {
    color: var(--accent-orange) !important;
  }
  
  &.active {
    color: var(--accent-orange) !important;
  }
`;

const SortIcon = styled.span`
  margin-left: 0.3rem;
  font-size: 0.8rem;
`;

const AthleteSeparatorRow = styled.tr`
  background: transparent;
  
  &:hover {
    background: transparent;
  }
`;

const AthleteSeparatorCell = styled.td`
  padding: 1rem 0.5rem 0.5rem 0;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  font-size: 1rem;
  font-weight: 900;
  border-bottom: 2px solid var(--accent-orange);
  border-top: 2px solid var(--accent-orange);
`;

const TypeBadge = styled.span<{ type: string }>`
  display: inline-block;
  padding: 0.15rem 0.4rem;
  border-radius: 4px;
  font-size: 0.55rem;
  font-weight: 700;
  text-transform: uppercase;
  background: var(--accent-orange);
  color: #fff;
`;

type SortField = 'name' | 'brand' | 'size' | 'type' | 'jumps';
type SortDirection = 'asc' | 'desc';

interface EquipmentProps {
  athleteId?: string; // Optional prop to filter by athlete and auto-assign
}

const Equipment: React.FC<EquipmentProps> = ({ athleteId: filterAthleteId }) => {
  const location = useLocation();
  const { user } = useAuth();
  const isJumperDashboard = user?.role === 'athlete';
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [expandedEquipment, setExpandedEquipment] = useState<Set<string>>(new Set());
  const [equipment, setEquipment] = useState<PopulatedEquipment[]>([]);
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedEquipmentType, setSelectedEquipmentType] = useState<'suit' | 'skis' | 'shoes' | 'bindings'>('suit');
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [editingEquipment, setEditingEquipment] = useState<PopulatedEquipment | null>(null);
  const [jumpCounts, setJumpCounts] = useState<Record<string, number>>({});
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const state = location.state as { openAddEquipment?: boolean } | null;
    if (state?.openAddEquipment) {
      setShowForm(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Load equipment, athletes, and jump counts from MongoDB
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        // Backend already filters equipment correctly based on user role and athlete relationships
        // No need for client-side filtering - the backend query handles everything
        const [equipmentData, athletesData, jumpCountsData] = await Promise.all([
          equipmentAPI.getAll(),
          athleteAPI.getAll(),
          equipmentAPI.getJumpCounts()
        ]);
        
        // Show all athletes and all equipment; lock athlete selection by plan in UI
        setEquipment(equipmentData);
        setAthletes(athletesData);
        setJumpCounts(jumpCountsData);
        setError(null);
      } catch (err) {
        setError('Failed to load equipment data. Please try again.');
        console.error('Error loading equipment data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const handleDeleteEquipment = async (id: string) => {
    // Add confirmation dialog
    const equipmentToDelete = equipment.find(item => item._id === id);
    const equipmentName = equipmentToDelete ? equipmentToDelete.name : 'this equipment';
    
    const isConfirmed = window.confirm(
      `Are you sure you want to delete "${equipmentName}"? This action cannot be undone.`
    );
    
    if (!isConfirmed) {
      return;
    }

    try {
      setDeletingIds(prev => new Set(prev).add(id));
      await equipmentAPI.delete(id);
      setEquipment(prev => prev.filter(item => item._id !== id));
      setError(null);
    } catch (err) {
      setError('Failed to delete equipment. Please try again.');
      console.error('Error deleting equipment:', err);
    } finally {
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const getEquipmentByType = (type: 'suit' | 'skis' | 'shoes' | 'bindings') => {
    return equipment.filter(item => item.type === type);
  };

  // Group equipment by athlete
  const getEquipmentByAthlete = () => {
    const equipmentByAthlete: Record<string, {
      athlete: Athlete | { _id: string; firstName: string; lastName: string } | null;
      equipment: PopulatedEquipment[];
    }> = {};

    // Add equipment assigned to athletes
    equipment.forEach(item => {
      const athleteId = item.athleteId 
        ? (typeof item.athleteId === 'object' ? item.athleteId._id : item.athleteId)
        : null;
      
      const athleteIdStr = athleteId ? String(athleteId) : 'unassigned';
      
      if (!equipmentByAthlete[athleteIdStr]) {
        if (athleteIdStr === 'unassigned') {
          equipmentByAthlete[athleteIdStr] = {
            athlete: null,
            equipment: []
          };
        } else {
          // Find athlete in the athletes list
          const athlete = athletes.find(a => a._id === athleteIdStr);
          equipmentByAthlete[athleteIdStr] = {
            athlete: athlete || (typeof item.athleteId === 'object' ? item.athleteId : null),
            equipment: []
          };
        }
      }
      
      equipmentByAthlete[athleteIdStr].equipment.push(item);
    });

    return equipmentByAthlete;
  };

  // Get equipment for a specific athlete and type
  const getEquipmentByAthleteAndType = (athleteId: string | null, type: 'suit' | 'skis' | 'shoes' | 'bindings') => {
    const athleteIdStr = athleteId ? String(athleteId) : 'unassigned';
    const equipmentByAthlete = getEquipmentByAthlete();
    const athleteEquipment = equipmentByAthlete[athleteIdStr]?.equipment || [];
    return athleteEquipment.filter(item => item.type === type);
  };

  // Get all athletes that have equipment (plus unassigned)
  const getAthletesWithEquipment = () => {
    const equipmentByAthlete = getEquipmentByAthlete();
    const athleteIds = Object.keys(equipmentByAthlete);
    
    return athleteIds.map(athleteId => {
      if (athleteId === 'unassigned') {
        return { _id: 'unassigned', firstName: 'Unassigned', lastName: '' };
      }
      const athlete = athletes.find(a => a._id === athleteId);
      return athlete || equipmentByAthlete[athleteId].athlete;
    }).filter(Boolean) as Array<Athlete | { _id: string; firstName: string; lastName: string }>;
  };

  const handleAddEquipment = (equipmentType: 'suit' | 'skis' | 'shoes' | 'bindings') => {
    setSelectedEquipmentType(equipmentType);
    setShowForm(true);
  };

  const handleEditEquipment = (equipmentItem: PopulatedEquipment) => {
    setEditingEquipment(equipmentItem);
    setSelectedEquipmentType(equipmentItem.type);
    setShowForm(true);
    // Scroll to form after a short delay to ensure it's rendered
    setTimeout(() => {
      const formElement = document.querySelector('[data-equipment-form]');
      formElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleSubmitEquipment = async (equipmentData: EquipmentFormData) => {
    try {
      // If filterAthleteId is provided and no athleteId is set (or empty string), auto-assign
      let athleteId = equipmentData.athleteId;
      if (filterAthleteId && (!athleteId || athleteId === '')) {
        athleteId = filterAthleteId;
      } else if (!athleteId || athleteId === '') {
        // If no athleteId and no filterAthleteId, set to null/undefined
        athleteId = undefined;
      }
      
      const equipmentDataToSubmit = {
        ...equipmentData,
        athleteId: athleteId || undefined // Convert empty string to undefined
      };
      
      if (editingEquipment) {
        // Update existing equipment
        const updatedEquipment = await equipmentAPI.update(editingEquipment._id, equipmentDataToSubmit);
        setEquipment(prev => prev.map(item => 
          item._id === editingEquipment._id ? updatedEquipment : item
        ));
      } else {
        // Create new equipment
        const newEquipment = await equipmentAPI.create(equipmentDataToSubmit);
        setEquipment(prev => [newEquipment, ...prev]);
      }
      setShowForm(false);
      setEditingEquipment(null);
      setError(null);
    } catch (err: any) {
      const errorMessage = err?.message || (editingEquipment ? 'Failed to update equipment. Please try again.' : 'Failed to add equipment. Please try again.');
      setError(errorMessage);
      console.error('Error saving equipment:', err);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingEquipment(null);
  };

  const getJumpCount = (equipmentId: string) => {
    // Ensure equipmentId is always a string for consistent lookup
    const idString = typeof equipmentId === 'string' ? equipmentId : String(equipmentId);
    const count = jumpCounts[idString] || 0;
    
    // Debug first few lookups to see if IDs match
    if (Object.keys(jumpCounts).length > 0 && count === 0) {
      console.log('Jump count lookup (no match found):', {
        equipmentId,
        idString,
        availableKeys: Object.keys(jumpCounts).slice(0, 5),
        count
      });
    }
    
    return count;
  };

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedEquipment = (equipmentList: PopulatedEquipment[]) => {
    return [...equipmentList].sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'name':
          comparison = (a.name || '').localeCompare(b.name || '');
          break;
        case 'brand':
          comparison = (a.brand || '').localeCompare(b.brand || '');
          break;
        case 'size':
          comparison = (a.size || '').localeCompare(b.size || '');
          break;
        case 'type':
          comparison = (a.type || '').localeCompare(b.type || '');
          break;
        case 'jumps':
          comparison = getJumpCount(a._id) - getJumpCount(b._id);
          break;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const getTypeLabel = (type: string) => {
    switch(type) {
      case 'suit': return 'Suit';
      case 'skis': return 'Skis';
      case 'shoes': return 'Shoes';
      case 'bindings': return 'Bindings';
      default: return type;
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return '↕';
    return sortDirection === 'asc' ? '↑' : '↓';
  };


  if (isLoading) {
    return (
      <Container>
        <PageTitle>Equipment</PageTitle>
        <LoadingMessage>Loading equipment...</LoadingMessage>
      </Container>
    );
  }

  const athletesWithEquipment = getAthletesWithEquipment();

  return (
    <Container>
      <PageTitle>Equipment</PageTitle>
      <PageSubtitle>Manage all your equipment in one place. Add new suits, skis, and other equipment.</PageSubtitle>

      {error && (
        <ErrorMessage>{error}</ErrorMessage>
      )}

      <div style={{ marginBottom: '1rem' }}>
        <AddNewEquipmentButton
          type="button"
          onClick={() => {
            setSelectedEquipmentType('suit');
            setShowForm(true);
          }}
          title="Add Equipment"
        >
          Add new equipment
        </AddNewEquipmentButton>
      </div>

      {/* Combined equipment table grouped by athlete */}
      <RecordsSection style={{
        width: '100%',
        maxWidth: '100%',
        marginLeft: 'auto',
        marginRight: 'auto',
        boxSizing: 'border-box',
        ...(isMobile ? {
          marginLeft: '-1rem',
          marginRight: '-1rem',
          paddingLeft: '1rem',
          paddingRight: '1rem',
          paddingTop: '1rem',
          paddingBottom: '1rem',
          borderRadius: '0'
        } : {})
      }}>
        {isMobile ? (
          // Mobile expandable list view
          <div>
            {athletesWithEquipment.length > 0 ? (
              athletesWithEquipment.map((athlete) => {
                const athleteId = athlete._id === 'unassigned' ? null : athlete._id;
                const athleteName = athlete._id === 'unassigned' 
                  ? 'Unassigned Equipment' 
                  : `${athlete.firstName} ${athlete.lastName}`;
                
                const athleteEquipment = getEquipmentByAthlete()[athleteId || 'unassigned']?.equipment || [];
                const sortedEquipment = getSortedEquipment(athleteEquipment);
                
                if (sortedEquipment.length === 0) return null;

                return (
                  <div key={athleteId || 'unassigned'} style={{ marginBottom: '1.5rem' }}>
                    <div style={{ 
                      fontWeight: 900, 
                      fontSize: '0.9rem', 
                      marginBottom: '0.75rem',
                      color: 'var(--accent-orange)',
                      fontFamily: "'Poppins', sans-serif",
                      paddingBottom: '0.5rem',
                      borderBottom: '2px solid var(--accent-orange)'
                    }}>
                      {athleteName}
                    </div>
                    {sortedEquipment.map((item) => {
                      const isExpanded = expandedEquipment.has(item._id);
                      return (
                        <div key={item._id} style={{ 
                          marginBottom: '0.75rem',
                          border: '1px solid #e0e6ed',
                          borderRadius: '8px',
                          overflow: 'hidden'
                        }}>
                          <div 
                            onClick={() => {
                              setExpandedEquipment(prev => {
                                const newSet = new Set(prev);
                                if (newSet.has(item._id)) {
                                  newSet.delete(item._id);
                                } else {
                                  newSet.add(item._id);
                                }
                                return newSet;
                              });
                            }}
                            style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              padding: '0.75rem',
                              cursor: 'pointer',
                              background: isExpanded ? 'var(--bg-sidebar)' : '#fff'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                              <TypeBadge type={item.type}>{getTypeLabel(item.type)}</TypeBadge>
                              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-gray)' }}>{item.name}</span>
                            </div>
                            <span style={{ fontSize: '0.9rem', color: 'var(--accent-orange)' }}>
                              {isExpanded ? '▼' : '▶'}
                            </span>
                          </div>
                          {isExpanded && (
                            <div style={{ padding: '0.75rem', background: '#fff', borderTop: '1px solid #e0e6ed' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.7rem' }}>
                                <div><strong>Brand:</strong> {item.brand || '-'}</div>
                                <div><strong>Size/Description:</strong> {item.size || '-'}</div>
                                <div><strong>Jumps:</strong> {getJumpCount(item._id)}</div>
                                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                                  <EditIcon 
                                    title="Edit" 
                                    onClick={() => handleEditEquipment(item)}
                                    style={{ marginRight: '0.5rem', fontSize: '0.9rem' }}
                                  >
                                    ✎
                                  </EditIcon>
                                  <DeleteIcon 
                                    title="Delete" 
                                    onClick={() => handleDeleteEquipment(item._id)}
                                    disabled={deletingIds.has(item._id)}
                                    style={{ 
                                      opacity: deletingIds.has(item._id) ? 0.5 : 1,
                                      cursor: deletingIds.has(item._id) ? 'not-allowed' : 'pointer',
                                      fontSize: '0.9rem'
                                    }}
                                  >
                                    {deletingIds.has(item._id) ? '...' : '✖'}
                                  </DeleteIcon>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })
            ) : (
              <EmptyMessage>No equipment added yet. Add equipment to get started.</EmptyMessage>
            )}
          </div>
        ) : (
          // Desktop table view
          <RecordsTable style={{ fontSize: '0.75rem', width: '100%', tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <SortableTableHeader 
                  style={{ fontSize: '0.65rem', fontWeight: 'bold', textAlign: 'center' }}
                  onClick={() => handleSort('type')}
                  className={sortField === 'type' ? 'active' : ''}
                >
                  Type <SortIcon>{getSortIcon('type')}</SortIcon>
                </SortableTableHeader>
                <SortableTableHeader 
                  style={{ fontSize: '0.65rem', fontWeight: 'bold', textAlign: 'center' }}
                  onClick={() => handleSort('name')}
                  className={sortField === 'name' ? 'active' : ''}
                >
                  Name <SortIcon>{getSortIcon('name')}</SortIcon>
                </SortableTableHeader>
                <SortableTableHeader 
                  style={{ fontSize: '0.65rem', fontWeight: 'bold', textAlign: 'center' }}
                  onClick={() => handleSort('brand')}
                  className={sortField === 'brand' ? 'active' : ''}
                >
                  Brand <SortIcon>{getSortIcon('brand')}</SortIcon>
                </SortableTableHeader>
                <SortableTableHeader 
                  style={{ fontSize: '0.65rem', fontWeight: 'bold', textAlign: 'center' }}
                  onClick={() => handleSort('size')}
                  className={sortField === 'size' ? 'active' : ''}
                >
                  Size/Description <SortIcon>{getSortIcon('size')}</SortIcon>
                </SortableTableHeader>
                <SortableTableHeader 
                  style={{ fontSize: '0.65rem', fontWeight: 'bold', textAlign: 'center' }}
                  onClick={() => handleSort('jumps')}
                  className={sortField === 'jumps' ? 'active' : ''}
                >
                  Jumps <SortIcon>{getSortIcon('jumps')}</SortIcon>
                </SortableTableHeader>
                <TableHeader style={{ fontSize: '0.65rem', fontWeight: 'bold', textAlign: 'center' }}>Actions</TableHeader>
              </tr>
            </thead>
            <tbody>
              {athletesWithEquipment.length > 0 ? (
                athletesWithEquipment.map((athlete) => {
                  const athleteId = athlete._id === 'unassigned' ? null : athlete._id;
                  const athleteName = athlete._id === 'unassigned' 
                    ? 'Unassigned Equipment' 
                    : `${athlete.firstName} ${athlete.lastName}`;
                  
                  // Get all equipment for this athlete
                  const athleteEquipment = getEquipmentByAthlete()[athleteId || 'unassigned']?.equipment || [];
                  const sortedEquipment = getSortedEquipment(athleteEquipment);
                  
                  if (sortedEquipment.length === 0) return null;

                  return (
                    <React.Fragment key={athleteId || 'unassigned'}>
                      {/* Athlete separator row */}
                      <AthleteSeparatorRow>
                        <AthleteSeparatorCell colSpan={6} style={{ paddingLeft: '1rem' }}>
                          {athleteName}
                        </AthleteSeparatorCell>
                      </AthleteSeparatorRow>
                      
                      {/* Equipment rows for this athlete */}
                      {sortedEquipment.map((item) => (
                        <TableRow key={item._id}>
                          <CenteredTableCell style={{ fontSize: '0.7rem', padding: '0.25rem' }}>
                            <TypeBadge type={item.type}>{getTypeLabel(item.type)}</TypeBadge>
                          </CenteredTableCell>
                          <CenteredTableCell style={{ fontSize: '0.7rem', padding: '0.25rem' }}>{item.name}</CenteredTableCell>
                          <CenteredTableCell style={{ fontSize: '0.7rem', padding: '0.25rem' }}>{item.brand || '-'}</CenteredTableCell>
                          <CenteredTableCell style={{ fontSize: '0.7rem', padding: '0.25rem' }}>{item.size || '-'}</CenteredTableCell>
                          <CenteredTableCell style={{ fontSize: '0.7rem', padding: '0.25rem' }}>
                            {getJumpCount(item._id)}
                          </CenteredTableCell>
                          <CenteredTableCell style={{ fontSize: '0.7rem', padding: '0.25rem' }}>
                            <EditIcon 
                              title="Edit" 
                              onClick={() => handleEditEquipment(item)}
                              style={{ marginRight: '0.5rem' }}
                            >
                              ✎
                            </EditIcon>
                            <DeleteIcon 
                              title="Delete" 
                              onClick={() => handleDeleteEquipment(item._id)}
                              disabled={deletingIds.has(item._id)}
                              style={{ 
                                opacity: deletingIds.has(item._id) ? 0.5 : 1,
                                cursor: deletingIds.has(item._id) ? 'not-allowed' : 'pointer'
                              }}
                            >
                              {deletingIds.has(item._id) ? '...' : '✖'}
                            </DeleteIcon>
                          </CenteredTableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                })
              ) : (
                <TableRow>
                  <CenteredTableCell colSpan={6} style={{ fontSize: '0.7rem', padding: '0.25rem' }}>
                    <EmptyMessage>No equipment added yet. Add equipment to get started.</EmptyMessage>
                  </CenteredTableCell>
                </TableRow>
              )}
            </tbody>
          </RecordsTable>
        )}
      </RecordsSection>

      {showForm && (
        <EquipmentForm
          onClose={handleCloseForm}
          onSubmit={handleSubmitEquipment}
          equipmentType={selectedEquipmentType}
          editingEquipment={editingEquipment}
          athleteId={filterAthleteId}
        />
      )}
    </Container>
  );
};

export default Equipment; 